import { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar,
} from 'recharts';
import AdminLayout from '@/components/feature/AdminLayout';
import { adminApi, type AnalyticsData } from '@/services/api';

const emptyData: AnalyticsData = {
  todayPv: 0,
  todayDau: 0,
  totalPv: 0,
  dailyTrend: [],
  topPages: [],
  topRegions: [],
};

export default function AdminAnalyticsPage() {
  const [days, setDays] = useState(14);
  const [data, setData] = useState<AnalyticsData>(emptyData);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    adminApi
      .analytics(days)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [days]);

  const trendData = data.dailyTrend.map((d) => ({
    ...d,
    label: d.date.slice(5),
  }));

  const topPagesData = data.topPages.slice(0, 10).map((p) => ({
    ...p,
    label: p.path.length > 24 ? p.path.slice(0, 24) + '…' : p.path,
  }));

  const topRegionsData = data.topRegions.slice(0, 15);

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground-950">统计分析</h2>
            <p className="text-sm text-foreground-500 mt-0.5">日活跃、页面访问分布</p>
          </div>
          <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-0.5">
            {[7, 14, 30].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  days === d ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                近{d}天
              </button>
            ))}
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
          <div className="bg-white rounded-xl p-4 border border-background-200">
            <span className="text-xs font-medium text-foreground-500">今日活跃(DAU)</span>
            <div className="text-2xl font-bold text-foreground-950 mt-2">{data.todayDau.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-background-200">
            <span className="text-xs font-medium text-foreground-500">今日浏览量(PV)</span>
            <div className="text-2xl font-bold text-foreground-950 mt-2">{data.todayPv.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-background-200">
            <span className="text-xs font-medium text-foreground-500">累计浏览量(PV)</span>
            <div className="text-2xl font-bold text-foreground-950 mt-2">{data.totalPv.toLocaleString()}</div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="w-10 h-10 mx-auto rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* DAU/PV trend */}
            <div className="bg-white rounded-xl border border-background-200 p-4 md:p-5">
              <h3 className="text-sm font-semibold text-foreground-950 mb-4">日活/浏览量趋势</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="dau" name="日活" stroke="#3b82f6" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="pv" name="浏览量" stroke="#f59e0b" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-center gap-6 mt-2">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm bg-blue-500" />
                  <span className="text-xs text-foreground-500">日活</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm bg-amber-500" />
                  <span className="text-xs text-foreground-500">浏览量</span>
                </div>
              </div>
            </div>

            {/* Page distribution */}
            <div className="bg-white rounded-xl border border-background-200 p-4 md:p-5">
              <h3 className="text-sm font-semibold text-foreground-950 mb-4">页面访问分布 Top 10</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topPagesData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={140} />
                    <Tooltip />
                    <Bar dataKey="pv" name="访问量" fill="#10b981" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* IP region distribution */}
            <div className="bg-white rounded-xl border border-background-200 p-4 md:p-5 lg:col-span-2">
              <h3 className="text-sm font-semibold text-foreground-950 mb-4">访客 IP 地域分布</h3>
              {topRegionsData.length === 0 ? (
                <p className="text-sm text-foreground-400 text-center py-10">暂无地域数据</p>
              ) : (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topRegionsData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                      <YAxis type="category" dataKey="region" tick={{ fontSize: 11 }} width={90} />
                      <Tooltip />
                      <Bar dataKey="pv" name="访问量" fill="#6366f1" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
